/**
 * AppRunHistory — the Run history panel (#184, D6(a)).
 *
 * The third tab of `DebugLogViewer`, and deliberately a SEPARATE panel rather
 * than a lane inside the Logs list. The two answer different questions and have
 * different trust properties:
 *
 *   Logs  — what the APP printed. Owned by the app, clearable by its author,
 *           and its ring buffer is shared with the app's own `console.log` noise.
 *   Runs  — what the PLATFORM observed around a script run. Not writable by the
 *           app, not clearable by anyone, and the only record of the failures
 *           that happen where the script cannot catch them.
 *
 * Merging them was considered and rejected: one lane would be clearable and the
 * other not, so a combined count would not add up to the rows beneath it.
 *
 * ── It fetches LAZILY, on first activation ──────────────────────────────────
 *
 * A separate endpoint from the debug log, so it is a separate request, and most
 * people opening this modal want the Logs tab. Mounting the component only when
 * the tab is selected would refetch on every tab switch; this stays mounted and
 * fetches once, which is what keeps a switch back instant.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { appRunLogApi, type AppRunEvent } from '@papan/files/client'
import { AlertCircle, RefreshCw, Clock, Zap, User, Copy, Check, ChevronDown } from 'lucide-react'
import { describeUa } from './ua.js'

interface AppRunHistoryProps {
  /** The app folder, or any file inside it — the server resolves and gates on
   *  the FOLDER either way. */
  fileId: string
  /** Whether this panel is the visible tab. Drives the lazy first fetch. */
  active: boolean
  /** Reported upward so the modal header can show a count without this
   *  component owning the header. `null` until the first load resolves — the
   *  header must not print "0 runs" for a fetch that has not happened. */
  onCount?: (n: number | null) => void
  /**
   * Bumped by the parent after it clears this app's run history, so the panel
   * re-reads instead of showing rows the server has just deleted.
   *
   * A TOKEN rather than a callback ref, so the parent does not have to hold a
   * handle to this component; and re-fetching rather than being told what to
   * remove, because a scoped clear (`?withinMs=`) is the server's decision about
   * which rows survive and guessing here would put a count on screen that the
   * next load contradicts.
   */
  refreshToken?: number
}

/**
 * How severe a kind is, which is the only thing the colour encodes.
 *
 * `platform` — nothing the author can fix: the container never started, exec
 * threw, the pool refused. Amber, because pointing an author at their own code
 * for our outage is worse than saying nothing.
 * `script` — their code, exit non-zero. Red.
 * `silent` — the run "succeeded" and something was still lost: events dropped,
 * db writes ignored, output truncated. Amber, and the most valuable rows here,
 * because nothing else anywhere reports them.
 */
const KIND_CLASS: Record<string, string> = {
  sandbox_acquire_failed: 'text-amber-500',
  sandbox_exec_failed: 'text-amber-500',
  too_many_concurrent_runs: 'text-amber-500',
  post_exec_failed: 'text-amber-500',
  app_events_dropped: 'text-amber-500',
  db_run_stderr_ignored: 'text-amber-500',
  output_truncated: 'text-amber-500',
  file_too_large: 'text-amber-500',
  files_not_saved: 'text-amber-500',
  hook_gave_up: 'text-amber-500',
  run_lock_lost: 'text-amber-500',
  db_writes_failed: 'text-amber-500',
  allowance_spent: 'text-amber-500',
  script_error: 'text-red-500',
  timeout: 'text-red-500',
}

function fmtWhen(ts: number): string {
  try {
    return new Date(ts).toLocaleString(undefined, {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short',
    })
  } catch {
    return String(ts)
  }
}

function fmtMs(ms: number | null): string | null {
  if (ms === null) return null
  if (ms < 1000) return `${ms}ms`
  const s = ms / 1000
  return s < 60 ? `${s.toFixed(1)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`
}

/**
 * Split a stderr tail into the EARLIER lines and the LAST one.
 *
 * The last line is the diagnosis — Python prints the exception after the frames
 * — so it is rendered outside the collapsible region and is always visible. See
 * the render site for the bug this shape exists to prevent.
 *
 * Cut by index into the original string rather than by joining a filtered array,
 * so blank lines and indentation inside the traceback survive expansion intact.
 */
function splitStderr(tail: string): { head: string; punchline: string; hasMore: boolean } {
  const trimmed = tail.replace(/\s+$/, '')
  const cut = trimmed.lastIndexOf('\n')
  const head = cut >= 0 ? trimmed.slice(0, cut) : ''
  const punchline = cut >= 0 ? trimmed.slice(cut + 1) : trimmed
  return { head, punchline, hasMore: head.trim().length > 0 }
}

/**
 * The collapsed excerpt fades out towards the chevron.
 *
 * A MASK rather than a background gradient: the row sits on a translucent glass
 * well whose colour differs per theme, so a gradient painted in one of those
 * colours would band against the other. A mask fades the text itself and is
 * backdrop-independent. It stops at 0.35 alpha instead of 0 — the last line is
 * the answer, and fading it to nothing to advertise that more exists would hide
 * the very thing the reader came for.
 */
const FADE_TO_CHEVRON = 'linear-gradient(to bottom, #000 45%, rgba(0, 0, 0, 0.35) 100%)'

/** The correlation id joins this row to the operator's `sandbox_start_failed`
 *  log line (#492). Copyable because its only use is being pasted somewhere. */
function CorrelationId({ id }: { id: string }) {
  const { t } = useTranslation('files')
  const [copied, setCopied] = useState(false)
  const copy = useCallback(() => {
    void navigator.clipboard?.writeText(id).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1500)
      },
      () => { /* clipboard denied — the id is still selectable as text */ },
    )
  }, [id])
  return (
    <button
      type="button"
      onClick={copy}
      title={t('runLog.copyRef')}
      className="inline-flex items-center gap-1 rounded px-1 font-mono text-[10px] themed-text-muted hover:themed-bg-tertiary"
    >
      {copied ? <Check size={10} className="text-green-500" /> : <Copy size={10} />}
      {id}
    </button>
  )
}

/**
 * WHO was using the app, in the words the Sessions tab already uses.
 *
 * Three outcomes, each a DIFFERENT fact, and collapsing them would be the kind
 * of plausible-looking wrong answer this feature exists to stop:
 *
 *   • a parsed agent   → "Chrome 126 · macOS". The author's real question about
 *                        a production failure is *is this everyone, or one old
 *                        browser?*, and only this answers it.
 *   • an agent we could not parse → we HAVE one, we just don't recognise it
 *                        (a bot, an exotic webview). The raw string rides the
 *                        row's title attribute, so it is one hover away.
 *   • none at all      → an UNATTENDED app-hook run has no browser, and so does
 *                        every row written before 0204 added the column. Saying
 *                        "not recorded" is true of both; inventing a browser
 *                        for either would not be.
 */
function whoLabel(r: AppRunEvent, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (r.trigger === 'app_hook') return t('runLog.trigger.app_hook')
  const parsed = describeUa(r.userAgent ?? undefined)
  if (parsed) return parsed
  return t(r.userAgent ? 'runLog.trigger.userUnrecognised' : 'runLog.trigger.userUnknown')
}

export function AppRunHistory({ fileId, active, onCount, refreshToken }: AppRunHistoryProps) {
  const { t } = useTranslation('files')
  const [runs, setRuns] = useState<AppRunEvent[]>([])
  const [truncated, setTruncated] = useState(false)
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<'forbidden' | 'failed' | null>(null)
  const [open, setOpen] = useState<Set<string>>(() => new Set())

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await appRunLogApi.get(fileId)
      setRuns(res.runs)
      setTruncated(res.truncated)
      setOpen(new Set())
      onCount?.(res.runs.length)
    } catch (e) {
      const status = (e as { status?: number })?.status
      setError(status === 403 ? 'forbidden' : 'failed')
      // Explicitly null, not 0: the header must not report "no failures" for a
      // request that never answered — which is the one claim this panel exists
      // to stop being made falsely.
      onCount?.(null)
    } finally {
      setLoading(false)
      setLoaded(true)
    }
  }, [fileId, onCount])

  // Lazy first fetch. Guarded on `loaded` rather than on a `mounted` flag:
  // <StrictMode> double-invokes effects in dev, and a mount-guard sees itself
  // already set on the second run. Guarding on the previous VALUE makes the
  // repeat a no-op instead of a second request.
  useEffect(() => {
    if (active && !loaded && !loading) void load()
  }, [active, loaded, loading, load])

  // A different app: drop everything and let the lazy fetch run again.
  useEffect(() => {
    setLoaded(false)
    setRuns([])
    setTruncated(false)
    setError(null)
  }, [fileId])

  // The parent cleared this app's history. Re-arm the lazy fetch rather than
  // emptying the list here — see `refreshToken`. Skipped on the initial value so
  // this never races the first load.
  const firstToken = useRef(refreshToken)
  useEffect(() => {
    if (refreshToken === firstToken.current) return
    firstToken.current = refreshToken
    setLoaded(false)
  }, [refreshToken])

  const toggle = useCallback((id: string) => {
    if (window.getSelection()?.toString()) return
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  if (loading && !loaded) {
    return <div className="themed-text-muted py-8 text-center">{t('runLog.loading')}</div>
  }
  if (error === 'forbidden') {
    return (
      <div className="themed-text-muted flex flex-col items-center gap-2 py-8 text-center">
        <AlertCircle size={20} className="text-amber-500" />
        {t('runLog.forbidden')}
      </div>
    )
  }
  if (error === 'failed') {
    return (
      <div className="themed-text-muted flex flex-col items-center gap-2 py-8 text-center">
        <div>{t('runLog.loadFailed')}</div>
        <button type="button" onClick={() => void load()} className="themed-btn-secondary inline-flex items-center gap-1 rounded px-2 py-1 text-xs">
          <RefreshCw size={12} /> {t('runLog.retry')}
        </button>
      </div>
    )
  }
  if (runs.length === 0) {
    // TWO sentences, and the second is the point. An empty list here is
    // ambiguous in a way an empty log is not — the reader cannot tell "this app
    // has not failed" from "nothing is being recorded". Saying that only
    // failures are stored resolves it, and stops an empty panel reading as a
    // broken feature.
    return (
      <div className="themed-text-muted flex flex-col items-center gap-1 py-8 text-center">
        <div className="themed-text-secondary">{t('runLog.emptyTitle')}</div>
        <div className="max-w-sm text-[11px]">{t('runLog.emptyBody')}</div>
      </div>
    )
  }

  return (
    <>
      {runs.map((r) => {
        const isOpen = open.has(r.id)
        const dur = fmtMs(r.durationMs)
        return (
          <div key={r.id} className="mb-2 rounded-lg ws-glass-well px-3 py-2 last:mb-1" data-testid="app-run-row">
            <div className="flex items-baseline gap-2">
              {/* The kind is the diagnosis and leads the row. Unknown kinds fall
                  through to the raw value rather than being hidden — the server
                  column is deliberately unconstrained so new kinds ship without
                  a migration, and this client must not assume its union is
                  complete. */}
              <span className={`shrink-0 font-medium ${KIND_CLASS[r.kind] ?? 'themed-text'}`}>
                {t(`runLog.kind.${r.kind}`, { defaultValue: r.kind })}
              </span>
              <span className="min-w-0 truncate themed-text-muted text-[10px]">{fmtWhen(r.at)}</span>
              <span className="ml-auto flex shrink-0 items-center gap-2 tabular-nums text-[10px] themed-text-muted">
                {dur && <span className="inline-flex items-center gap-0.5"><Clock size={10} />{dur}</span>}
                {/* `null` exit code means the script NEVER RAN — rendered as an
                    absence, never as 0, which would read as a clean exit. */}
                {r.exitCode !== null && <span>exit {r.exitCode}</span>}
              </span>
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] themed-text-secondary">
              {/*
                * WHO was using the app, named the same way the Sessions tab
                * names them — one `describeUa`, so the two tabs cannot describe
                * the same person differently inside one modal.
                *
                * "Chrome 126 · macOS" answers the author's real question about
                * a production failure: is this everyone, or one old browser?
                * The generic trigger copy is the FALLBACK, not the default —
                * it is what an unattended hook (no browser at all), an
                * unparseable agent, and every row written before 0204
                * correctly read as. The raw string rides the title attribute
                * rather than the row, so a 120-char UA can never wreck the
                * layout.
                */}
              <span className="inline-flex items-center gap-1" title={r.userAgent ?? undefined}>
                {r.trigger === 'app_hook' ? <Zap size={10} /> : <User size={10} />}
                {whoLabel(r, t)}
              </span>
              {r.scriptPath && <span className="min-w-0 truncate font-mono">{r.scriptPath}</span>}
              {/* Shown only when it says something `kind` did not. They agree on
                  most rows; where they differ (an exhausted vanish-retry and a
                  genuine exec failure share one client-facing code) the pair IS
                  the diagnosis. */}
              {r.errorCode && r.errorCode !== r.kind && (
                <span className="font-mono themed-text-muted">{r.errorCode}</span>
              )}
              {r.correlationId && <CorrelationId id={r.correlationId} />}
            </div>

            {r.stderrTail && (() => {
              /**
               * THE LAST LINE LIVES OUTSIDE THE COLLAPSIBLE REGION.
               *
               * This started as a 3-line clamp and was wrong in the most
               * expensive possible way — visible only against a real traceback.
               * Python prints the exception LAST, so a head-clamp renders
               * "Traceback (most recent call last):" plus two frames of file
               * paths and cuts off `ValueError: could not convert string to
               * float: 'N/A'` — the single line the author actually needs, and
               * the entire reason D3(b) stores this field rather than D3(a)'s
               * outcome-only row. The panel looked fine and answered nothing.
               *
               * Splitting the text at the last line, rather than clamping it,
               * makes that structural: the earlier frames are the only thing
               * the toggle can ever hide, so no future styling change can bury
               * the diagnosis again. `AppRunHistory.test.tsx` asserts the
               * punchline is not a descendant of the collapsible region for
               * exactly this reason.
               *
               * Same reasoning as the writer keeping the TAIL of an oversized
               * stderr rather than its head, applied one level up at render.
               */
              const { head, punchline, hasMore } = splitStderr(r.stderrTail)
              const label = isOpen ? t('runLog.showLess') : t('runLog.showFull')
              return (
                <div
                  onClick={hasMore ? () => toggle(r.id) : undefined}
                  className={`mt-1.5 rounded font-mono text-[10px] themed-text-muted ${hasMore ? 'cursor-pointer' : ''}`}
                >
                  {/*
                    * The earlier frames, revealed by GROWING rather than
                    * appearing. `grid-template-rows: 0fr → 1fr` is the one
                    * transition that animates to a height nobody measured — a
                    * `max-height` guess either clips a long traceback or eases
                    * across empty space it never fills. The child owns the
                    * `overflow-hidden`; the grid row owns the animation.
                    *
                    * Kept MOUNTED in both states, because a conditionally
                    * rendered child cannot animate away — it would vanish and
                    * leave an empty box to collapse. `aria-hidden` is what
                    * keeps it out of the accessibility tree meanwhile.
                    */}
                  {hasMore && (
                    <div
                      id={`stderr-${r.id}`}
                      data-testid="stderr-head"
                      aria-hidden={!isOpen}
                      className={`grid transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                    >
                      <div className="min-h-0 overflow-hidden">
                        <div className="whitespace-pre-wrap break-words">{head}</div>
                      </div>
                    </div>
                  )}

                  <div
                    data-testid="stderr-punchline"
                    className={`break-words ${isOpen ? 'whitespace-pre-wrap' : 'line-clamp-2'}`}
                    style={hasMore && !isOpen ? { maskImage: FADE_TO_CHEVRON, WebkitMaskImage: FADE_TO_CHEVRON } : undefined}
                  >
                    {punchline}
                  </div>

                  {/*
                    * The affordance is a chevron under a fade, not a worded
                    * link: it is what every disclosure on the web looks like,
                    * so it needs no reading. It is still a real <button> with
                    * an accessible name and `aria-expanded`, because an icon
                    * that only LOOKS like a control is one screen readers
                    * cannot use — and the collapsed view is deliberately
                    * INCOMPLETE, so a reader who never finds the expand is
                    * reading a partial answer believing it is whole.
                    *
                    * No `onClick` of its own: the click bubbles to the block
                    * above, which already toggles. Two handlers on nested
                    * elements would toggle twice and appear to do nothing.
                    */}
                  {hasMore && (
                    <button
                      type="button"
                      data-testid="stderr-toggle"
                      aria-expanded={isOpen}
                      aria-controls={`stderr-${r.id}`}
                      aria-label={label}
                      title={label}
                      className="flex w-full items-center justify-center py-0.5 opacity-50 transition-opacity hover:opacity-100"
                    >
                      <ChevronDown
                        size={12}
                        aria-hidden
                        className={`transition-transform duration-200 motion-reduce:transition-none ${isOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                  )}
                </div>
              )
            })()}
          </div>
        )
      })}
      {truncated && (
        // Stated by the server, not inferred from a full page — see the route.
        <div className="themed-text-muted py-2 text-center text-[10px]">{t('runLog.truncated')}</div>
      )}
    </>
  )
}
